import { NavLink as RouterNavLink, NavLinkProps, useNavigate } from "react-router-dom";
import { forwardRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import { useGuestExitPreventionOptional } from "@/contexts/GuestExitPreventionContext";

interface NavLinkCompatProps extends Omit<NavLinkProps, "className"> {
  className?: string;
  activeClassName?: string;
  pendingClassName?: string;
  /** Skip guest exit prevention check (e.g., for auth links that handle it separately) */
  skipExitPrevention?: boolean;
}

/**
 * NavLink wrapper that integrates with guest exit prevention
 * 
 * When a guest has an active chat session on /chat, clicking any NavLink
 * will show the exit prevention modal instead of navigating immediately.
 */
const NavLink = forwardRef<HTMLAnchorElement, NavLinkCompatProps>(
  ({ className, activeClassName, pendingClassName, to, onClick, skipExitPrevention, ...props }, ref) => {
    const guestExitPrevention = useGuestExitPreventionOptional();
    
    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        // Call original onClick if provided
        onClick?.(e);
        
        // If already prevented, don't do anything else
        if (e.defaultPrevented) return;
        
        // Skip exit prevention if explicitly disabled
        if (skipExitPrevention) return;
        
        // Get the path string from 'to' prop
        const path = typeof to === 'string' ? to : to.pathname || '';
        
        // Check if guest exit prevention should intercept
        if (guestExitPrevention?.requestNavigation(path)) {
          e.preventDefault();
        }
      },
      [onClick, to, guestExitPrevention, skipExitPrevention]
    );

    return (
      <RouterNavLink
        ref={ref}
        to={to}
        onClick={handleClick}
        className={({ isActive, isPending }) =>
          cn(className, isActive && activeClassName, isPending && pendingClassName)
        }
        {...props}
      />
    );
  },
);

NavLink.displayName = "NavLink";

export { NavLink };
